import { AnalyticsUserEvent } from '@fruitful/types';
import Clipboard from '@react-native-clipboard/clipboard';
import { Link } from 'expo-router';
import * as React from 'react';
import { Share, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CircleDollarSignIcon,
    CONTENT_MAX_WIDTH,
    H2Regular,
    Icon,
    type IconType,
    Image,
    isNonMobileWeb,
    LinkIcon,
    ListCheckIcon,
    SmoothieScrollView,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import fruitfulGardenImage from '../../../assets/images/backgrounds/orchard.png';
import { track } from '../../logger/Logger';
import { ANNUAL_REFERRAL_LIMIT } from '../utils/referralsUtils';

const BackgroundImage = ({
    position,
    topSafeAreaInset,
    height,
}: {
    position: 'absolute' | 'relative';
    topSafeAreaInset: number;
    height: number;
}) => {
    return (
        <View
            height={topSafeAreaInset + height}
            top={isNonMobileWeb ? 24 : 0}
            left={0}
            width="100%"
            position={position}
            overflow="hidden"
            borderRadius={isNonMobileWeb ? 24 : 0}
        >
            <Image
                source={fruitfulGardenImage}
                objectFit="cover"
                resizeMode="cover"
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: isNonMobileWeb ? 24 : 0,
                }}
            />
        </View>
    );
};

interface RequirementRowProps {
    icon: IconType;
    title: string;
    description: string;
}

const RequirementRow = ({ icon, title, description }: RequirementRowProps) => {
    return (
        <XStack
            backgroundColor="$gray100"
            borderRadius={12}
            padding={16}
            gap={12}
            alignItems="flex-start"
        >
            <View paddingTop={2}>
                <Icon icon={icon} size={20} color="$black" />
            </View>
            <YStack flex={1} gap={4}>
                <BodyMediumRegular>{title}</BodyMediumRegular>
                <BodySmallRegular color="$gray1000">
                    {description}
                </BodySmallRegular>
            </YStack>
        </XStack>
    );
};

interface ReferralsProps {
    referralCode: string;
    currentYearReferralCount: number;
}

export const Referrals = ({
    referralCode,
    currentYearReferralCount,
}: ReferralsProps) => {
    const toast = useToastController();
    const { top: topSafeAreaInset } = useSafeAreaInsets();
    const { height: screenHeight } = useWindowDimensions();

    const backgroundImageHeight = !isNonMobileWeb ? 400 : screenHeight * 0.3;

    const referralLink = `https://fruitful.com/referred-by?referralCode=${encodeURIComponent(referralCode)}`;

    const handleInviteFriends = async () => {
        track(AnalyticsUserEvent.REFERRALS_INVITE_FRIENDS_PRESSED);

        try {
            await Share.share(
                {
                    title: 'Join me on Fruitful!',
                    message: `I've been loving Fruitful, it's leveled up my finances! 🌱 Use my link to join, and we can both earn $100!`,
                    url: referralLink,
                },
                {
                    subject: 'Join me on Fruitful!',
                },
            );
        } catch (error) {
            // User cancelled or share not supported - do nothing and let the user resolve
        }
    };

    const handleCopyInviteLink = () => {
        track(AnalyticsUserEvent.REFERRALS_COPY_LINK_PRESSED);
        Clipboard.setString(referralLink);
        toast.show('Link copied!', { duration: 2000 });
    };

    const referralsLeft = ANNUAL_REFERRAL_LIMIT - currentYearReferralCount;

    return (
        <YStack flex={1} backgroundColor="$white">
            {!isNonMobileWeb && (
                <BackgroundImage
                    position="absolute"
                    topSafeAreaInset={topSafeAreaInset}
                    height={backgroundImageHeight}
                />
            )}
            <SmoothieScrollView
                containerStyle={{
                    paddingBottom: 32,
                    paddingHorizontal: 0,
                    marginHorizontal: 0,
                    marginTop: '0 !important',
                    width: '100%',
                    maxWidth: isNonMobileWeb ? CONTENT_MAX_WIDTH : '100%',
                    alignSelf: 'center',
                }}
            >
                {/* Illustration Section with back button overlay */}
                {isNonMobileWeb && (
                    <BackgroundImage
                        position="relative"
                        topSafeAreaInset={topSafeAreaInset}
                        height={backgroundImageHeight}
                    />
                )}
                <YStack
                    marginTop={
                        isNonMobileWeb ? 32 : backgroundImageHeight * 0.8
                    }
                >
                    <View
                        backgroundColor="$white"
                        borderTopLeftRadius={isNonMobileWeb ? 0 : 24}
                        borderTopRightRadius={isNonMobileWeb ? 0 : 24}
                        marginTop={isNonMobileWeb ? 0 : -24}
                        paddingTop={32}
                        paddingBottom={32}
                        paddingHorizontal={24}
                        gap={32}
                    >
                        {/* Main Heading */}
                        <H2Regular>Give $100, Get $100!</H2Regular>

                        <YStack gap={16}>
                            <BodyMediumEmphasized>
                                It&apos;s simple
                            </BodyMediumEmphasized>
                            <YStack gap={12}>
                                <RequirementRow
                                    icon={ListCheckIcon}
                                    title="Invite your friends"
                                    description="Send your link to anyone who'd love Fruitful."
                                />
                                <RequirementRow
                                    icon={CircleDollarSignIcon}
                                    title="$100 for you and your friend"
                                    description="Once they've been a member for 45 days and had 3 meetings, you both earn $100."
                                />
                            </YStack>
                        </YStack>

                        <YStack gap={4}>
                            <BodySmallRegular
                                color="$gray1000"
                                textAlign="center"
                            >
                                Max {ANNUAL_REFERRAL_LIMIT} referrals per year.
                            </BodySmallRegular>
                            <Link
                                href="https://fruitful.com/legal/referral"
                                target="_blank"
                                style={{ textAlign: 'center' }}
                            >
                                <BodySmallRegular
                                    color="$gray1000"
                                    textDecorationLine="underline"
                                >
                                    See terms.
                                </BodySmallRegular>
                            </Link>
                        </YStack>

                        {/* Action Buttons */}
                        {referralsLeft > 0 && (
                            <YStack gap={12}>
                                <Button
                                    text="Invite friends"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    onPress={handleInviteFriends}
                                    width="full"
                                />
                                <XStack
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={8}
                                    onPress={handleCopyInviteLink}
                                    cursor="pointer"
                                    paddingVertical={8}
                                >
                                    <Icon
                                        icon={LinkIcon}
                                        size={16}
                                        color="$black"
                                    />
                                    <BodyMediumRegular>
                                        Copy invite link
                                    </BodyMediumRegular>
                                </XStack>
                            </YStack>
                        )}
                    </View>
                </YStack>
            </SmoothieScrollView>
            {/* Back button positioned over illustration */}
            <View
                position="absolute"
                top={Math.max(topSafeAreaInset, 16)}
                left={16}
            >
                <BackButton
                    iconType={BackButtonIconType.ARROW_LEFT}
                    fallbackRoute="/home"
                />
            </View>
        </YStack>
    );
};
